import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, Check, Link2, Loader2, Ban } from 'lucide-react';
import {
  affiliatesApi,
  affiliateLinkUrl,
  describeAffiliateIncentive,
  describeAffiliateReward,
  type AffiliateJoinPlanCode,
  type AffiliateLinkWithStats,
  type AffiliateRewardType,
  type CreateAffiliateLinkInput,
} from '@/api/affiliates';
import { orgsApi } from '@/api/orgs';
import { formatDate } from '@/utils/date';

const PLANS: { value: AffiliateJoinPlanCode; label: string }[] = [
  { value: 'ignite', label: 'Ignite' },
  { value: 'accelerate', label: 'Accelerate' },
  { value: 'command', label: 'Command' },
  { value: 'business', label: 'Business' },
];

const REWARD_TYPES: { value: AffiliateRewardType; label: string }[] = [
  { value: 'sub_discount', label: 'Revenue share (% / months)' },
  { value: 'ai_credits', label: 'AI credits' },
  { value: 'sub_credit_ils', label: 'Subscription credit (₪)' },
  { value: 'none', label: 'None' },
];

function referrerLabel(link: AffiliateLinkWithStats): string {
  if (link.referrer_org) return link.referrer_org.name;
  if (link.partner_name) return link.partner_name;
  return '—';
}

function CopyUrlButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const url = affiliateLinkUrl(code);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 text-xs"
      title={url}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      Copy URL
    </button>
  );
}

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        active ? 'text-green-400 bg-green-400/10' : 'text-gray-400 bg-gray-400/10'
      }`}
    >
      {active ? 'Active' : 'Disabled'}
    </span>
  );
}

type FormState = {
  label: string;
  code: string;
  referrerMode: 'org' | 'partner' | 'none';
  referrerOrgId: string;
  partnerName: string;
  rewardType: AffiliateRewardType;
  rewardDiscountPercent: string;
  rewardDiscountMonths: string;
  rewardAiCredits: string;
  rewardCreditNis: string;
  joinPlanCode: AffiliateJoinPlanCode;
  joinFreeDays: string;
  maxUses: string;
  expiresAt: string;
};

function defaultForm(prefillOrgId?: string): FormState {
  return {
    label: '',
    code: '',
    referrerMode: prefillOrgId ? 'org' : 'none',
    referrerOrgId: prefillOrgId ?? '',
    partnerName: '',
    rewardType: 'sub_discount',
    rewardDiscountPercent: '5',
    rewardDiscountMonths: '12',
    rewardAiCredits: '500',
    rewardCreditNis: '50',
    joinPlanCode: 'ignite',
    joinFreeDays: '30',
    maxUses: '',
    expiresAt: '',
  };
}

function CreateLinkCard({
  prefillOrgId,
  prefillOrgName,
  onCreated,
}: {
  prefillOrgId?: string;
  prefillOrgName?: string;
  onCreated?: (link: { code: string }) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => defaultForm(prefillOrgId));
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [orgSearch, setOrgSearch] = useState(prefillOrgName ?? '');

  const { data: orgResults } = useQuery({
    queryKey: ['affiliate-org-search', orgSearch],
    queryFn: () => orgsApi.list({ search: orgSearch, perPage: 8 }),
    enabled: form.referrerMode === 'org' && orgSearch.trim().length >= 2,
  });
  const orgItems = orgResults?.items ?? [];

  const createMut = useMutation({
    mutationFn: (input: CreateAffiliateLinkInput) => affiliatesApi.createLink(input),
    onSuccess: (link) => {
      toast.success('Affiliate link created');
      const url = affiliateLinkUrl(link.code);
      setCreatedUrl(url);
      onCreated?.(link);
      queryClient.invalidateQueries({ queryKey: ['affiliate-links'] });
      setForm(defaultForm(prefillOrgId));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submit = () => {
    const payload: CreateAffiliateLinkInput = {
      label: form.label,
      code: form.code,
      reward_type: form.rewardType,
      join_plan_code: form.joinPlanCode,
      join_free_days: parseInt(form.joinFreeDays, 10) || 30,
      max_uses: form.maxUses ? parseInt(form.maxUses, 10) : null,
      expires_at: form.expiresAt || null,
    };

    if (form.referrerMode === 'org' && form.referrerOrgId) {
      payload.referrer_org_id = form.referrerOrgId;
    } else if (form.referrerMode === 'partner' && form.partnerName.trim()) {
      payload.partner_name = form.partnerName.trim();
    }

    if (form.rewardType === 'sub_discount') {
      payload.reward_discount_percent = parseInt(form.rewardDiscountPercent, 10) || 5;
      payload.reward_discount_months = parseInt(form.rewardDiscountMonths, 10) || 12;
    } else if (form.rewardType === 'ai_credits') {
      payload.reward_ai_credits = parseInt(form.rewardAiCredits, 10) || 0;
    } else if (form.rewardType === 'sub_credit_ils') {
      payload.reward_credit_agorot = Math.round(parseFloat(form.rewardCreditNis) * 100) || 0;
    }

    createMut.mutate(payload);
  };

  return (
    <div className="bg-navy-card border border-navy-border rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-indigo-400" />
        <h3 className="text-sm font-semibold text-ink-primary">Create affiliate link</h3>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-ink-secondary block mb-1">Label</label>
          <input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
            placeholder="Campaign name"
          />
        </div>
        <div>
          <label className="text-xs text-ink-secondary block mb-1">Code (4–20 A-Z, 0-9)</label>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm font-mono uppercase"
            dir="ltr"
            placeholder="PARTNER1"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs text-ink-secondary block mb-1">Referrer</label>
          <select
            value={form.referrerMode}
            onChange={(e) =>
              setForm({ ...form, referrerMode: e.target.value as FormState['referrerMode'] })
            }
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
          >
            <option value="none">None / global</option>
            <option value="org">Organization</option>
            <option value="partner">External partner</option>
          </select>
        </div>
        {form.referrerMode === 'org' && (
          <div className="md:col-span-2">
            <label className="text-xs text-ink-secondary block mb-1">Referrer org</label>
            <input
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
              placeholder="Search org name…"
            />
            {prefillOrgId && form.referrerOrgId === prefillOrgId && prefillOrgName && (
              <p className="text-xs text-ink-secondary mt-1">Prefilled: {prefillOrgName}</p>
            )}
            {orgItems.length > 0 && orgSearch.length >= 2 && (
              <ul className="mt-1 border border-navy-border rounded-lg overflow-hidden max-h-32 overflow-y-auto">
                {orgItems.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 text-ink-primary"
                      onClick={() => {
                        setForm({ ...form, referrerOrgId: o.id });
                        setOrgSearch(o.name);
                      }}
                    >
                      {o.name}
                      <span className="text-ink-secondary text-xs ml-2 font-mono">{o.slug}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {form.referrerMode === 'partner' && (
          <div className="md:col-span-2">
            <label className="text-xs text-ink-secondary block mb-1">Partner name</label>
            <input
              value={form.partnerName}
              onChange={(e) => setForm({ ...form, partnerName: e.target.value })}
              className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
            />
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs text-ink-secondary block mb-1">Reward type</label>
          <select
            value={form.rewardType}
            onChange={(e) =>
              setForm({ ...form, rewardType: e.target.value as AffiliateRewardType })
            }
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
          >
            {REWARD_TYPES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        {form.rewardType === 'sub_discount' && (
          <>
            <div>
              <label className="text-xs text-ink-secondary block mb-1">Share %</label>
              <input
                type="number"
                min={1}
                max={100}
                value={form.rewardDiscountPercent}
                onChange={(e) => setForm({ ...form, rewardDiscountPercent: e.target.value })}
                className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-ink-secondary block mb-1">Months</label>
              <input
                type="number"
                min={1}
                value={form.rewardDiscountMonths}
                onChange={(e) => setForm({ ...form, rewardDiscountMonths: e.target.value })}
                className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
              />
            </div>
          </>
        )}
        {form.rewardType === 'ai_credits' && (
          <div className="md:col-span-2">
            <label className="text-xs text-ink-secondary block mb-1">AI credits</label>
            <input
              type="number"
              min={1}
              value={form.rewardAiCredits}
              onChange={(e) => setForm({ ...form, rewardAiCredits: e.target.value })}
              className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
            />
          </div>
        )}
        {form.rewardType === 'sub_credit_ils' && (
          <div className="md:col-span-2">
            <label className="text-xs text-ink-secondary block mb-1">Credit (₪)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.rewardCreditNis}
              onChange={(e) => setForm({ ...form, rewardCreditNis: e.target.value })}
              className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
            />
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div>
          <label className="text-xs text-ink-secondary block mb-1">New-org plan</label>
          <select
            value={form.joinPlanCode}
            onChange={(e) =>
              setForm({ ...form, joinPlanCode: e.target.value as AffiliateJoinPlanCode })
            }
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
          >
            {PLANS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-ink-secondary block mb-1">Free days</label>
          <input
            type="number"
            min={1}
            value={form.joinFreeDays}
            onChange={(e) => setForm({ ...form, joinFreeDays: e.target.value })}
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-ink-secondary block mb-1">Max uses</label>
          <input
            type="number"
            min={1}
            value={form.maxUses}
            onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
            placeholder="∞"
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-ink-secondary block mb-1">Expires</label>
          <input
            type="date"
            value={form.expiresAt}
            onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={createMut.isPending || !form.label.trim() || !form.code.trim()}
          className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg px-4 py-2 flex items-center gap-2"
        >
          {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Generate link
        </button>
        {createdUrl && (
          <code className="text-xs text-emerald-400 font-mono truncate max-w-md" title={createdUrl}>
            {createdUrl}
          </code>
        )}
      </div>
    </div>
  );
}

function LinksTable({ links, isLoading }: { links: AffiliateLinkWithStats[]; isLoading: boolean }) {
  const queryClient = useQueryClient();

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      affiliatesApi.toggleActive(id, active),
    onSuccess: () => {
      toast.success('Link updated');
      queryClient.invalidateQueries({ queryKey: ['affiliate-links'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="py-8 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (!links.length) {
    return <div className="py-10 text-center text-ink-secondary text-sm">No affiliate links yet</div>;
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-navy-border text-xs text-ink-secondary">
          <th className="text-left px-5 py-2.5 font-medium">Code</th>
          <th className="text-left px-4 py-2.5 font-medium">Referrer</th>
          <th className="text-left px-4 py-2.5 font-medium">Reward</th>
          <th className="text-left px-4 py-2.5 font-medium">Incentive</th>
          <th className="text-left px-4 py-2.5 font-medium">Uses</th>
          <th className="text-left px-4 py-2.5 font-medium">Paid conv.</th>
          <th className="text-left px-4 py-2.5 font-medium">Status</th>
          <th className="px-4 py-2.5" />
        </tr>
      </thead>
      <tbody>
        {links.map((link) => (
          <tr key={link.id} className="border-b border-navy-border/50 hover:bg-white/[0.02]">
            <td className="px-5 py-3 font-mono text-ink-primary">{link.code}</td>
            <td className="px-4 py-3 text-ink-primary">{referrerLabel(link)}</td>
            <td className="px-4 py-3 text-ink-secondary">{describeAffiliateReward(link)}</td>
            <td className="px-4 py-3 text-ink-secondary">{describeAffiliateIncentive(link)}</td>
            <td className="px-4 py-3 font-latin text-ink-primary">
              {link.use_count}
              {link.max_uses != null ? ` / ${link.max_uses}` : ''}
            </td>
            <td className="px-4 py-3 font-latin text-ink-primary">{link.paid_conversions}</td>
            <td className="px-4 py-3">
              <StatusPill active={link.is_active} />
              {link.expires_at && (
                <div className="text-xs text-ink-secondary mt-0.5">{formatDate(link.expires_at)}</div>
              )}
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-3">
                <CopyUrlButton code={link.code} />
                {link.is_active && (
                  <button
                    type="button"
                    onClick={() => toggleMut.mutate({ id: link.id, active: false })}
                    className="text-ink-secondary hover:text-red-400 flex items-center gap-1 text-xs"
                    title="Disable link"
                  >
                    <Ban size={14} />
                    Disable
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AffiliateLinksPanel({
  referrerOrgId,
  referrerOrgName,
  showCreate = true,
}: {
  referrerOrgId?: string;
  referrerOrgName?: string;
  showCreate?: boolean;
}) {
  const { data: links = [], isLoading } = useQuery({
    queryKey: ['affiliate-links', referrerOrgId ?? 'all'],
    queryFn: () =>
      affiliatesApi.listLinks(referrerOrgId ? { referrerOrgId } : undefined),
  });

  return (
    <div className="space-y-4">
      {showCreate && (
        <CreateLinkCard prefillOrgId={referrerOrgId} prefillOrgName={referrerOrgName} />
      )}
      <div className="bg-navy-card border border-navy-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-navy-border flex items-center gap-2">
          <Link2 className="h-4 w-4 text-ink-secondary" />
          <h3 className="text-sm font-semibold text-ink-primary">Affiliate links</h3>
          <span className="text-xs text-ink-secondary ml-auto">{links.length} links</span>
        </div>
        <LinksTable links={links} isLoading={isLoading} />
      </div>
    </div>
  );
}
